import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import Logo from "../components/Logo";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";
  const searchParams = new URLSearchParams(location.search);
  const oauthError = searchParams.get("error") === "google";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      navigate(user.role === "admin" ? "/admin" : from);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="w-full max-w-md">
        <div className="flex justify-center mb-7"><Logo size="lg" /></div>
        <div className="premium-card p-8 sm:p-10">
          <h1 className="font-serif-display text-3xl text-brand-text">Welcome back</h1>
          <p className="text-brand-muted mt-1.5">Sign in to support, share, and uplift.</p>

          {oauthError && (
            <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
              Google sign-in didn't complete. Please try again or use your email & password.
            </p>
          )}

          <div className="mt-7">
            <GoogleSignInButton />
          </div>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-brand-border" />
            <span className="text-xs uppercase tracking-widest text-brand-muted">or</span>
            <div className="h-px flex-1 bg-brand-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.6} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="input-premium !pl-11" placeholder="you@example.com" required
                       data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2 flex items-center justify-between">
                <span>Password</span>
                <Link
                  to="/forgot-password"
                  className="text-[0.7rem] uppercase tracking-widest text-brand-primary hover:text-brand-wine font-semibold normal-case sm:tracking-wider"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" strokeWidth={1.6} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="input-premium !pl-11" placeholder="••••••••" required
                       data-testid="login-password-input" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full !py-3.5" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} strokeWidth={1.8} className="ml-2" />
            </button>
          </form>

          <p className="text-center text-sm text-brand-muted mt-6">
            New to Dkarez? <Link to="/register" className="text-brand-primary font-medium hover:underline" data-testid="login-to-register-link">Create an account</Link>
          </p>
        </div>
        <p className="text-center text-xs text-brand-muted mt-5">
          Demo admin: <span className="font-mono">admin@dkarez.org / Admin@123</span>
        </p>
      </motion.div>
    </div>
  );
}
